/**
 * CopyPlanStep — Step 2 of CopyCampaignWorkflow ("Confirm & adjust").
 *
 * ONE step does both jobs. Confirming what comes along used to be a separate
 * step before this one (CopyPickerStep, a source-side checkbox tree that
 * pre-filtered ids into the build request); it is gone, because the grid below
 * already lists every campaign / ad group / line item the copy will create and
 * deleting a row here is the better exclusion — it edits the REAL built
 * template instead of guessing from the source, and it saves the user reading
 * the same list twice.
 *
 * Build-then-review: entering this step is what fires the planning-mode
 * builds — the container POSTs /copy with planningMode for every source
 * campaign as the user leaves Describe (see
 * CopyCampaignWorkflow.js's startBuilds) and each build lands here as a REAL
 * launcher template (GET /planning-review's launcherResult) — the exact
 * object the platform launch will consume. This replaces BOTH of the old
 * bespoke grids (the draft.html-derived GoogleSearchPlan and the
 * preview-backed DspPlan): every platform now renders through CLW's shared
 * <ReviewGrid> + <CellDetailPanel> over rows built from the built template
 * (copyReviewEdits.js's buildCopyReviewRows), wrapped in a `.clw` ancestor as
 * those components' own doc comments require.
 *
 * Edits are REAL: committing a cell applies the change onto a clone of the
 * launcherResult (applyCopyReviewEdits — every editable field in
 * copyGridConstants.js has a write-back applier) and PUTs it to
 * /planning-review via the container's onSaveBuild, one save per commit (CLW
 * versions each PUT server-side, so no client batching is needed). The push
 * step launches the SAVED template.
 *
 * Gates are the backend's copy_gates.py rows (launcherResult.gates), already
 * re-checked against the current template by the container's resolveGates —
 * rendered above each build's grid with the "use a unique name" quick-fix for
 * duplicate_name (which writes through the same applier+save path). The old
 * client-side value-substring gate resolver, bulk find & replace panel (its
 * cellOverrides plumbing is gone), decorative readOnly row checkboxes and
 * pendingDeletions dead code were all deleted with the bespoke grids.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReviewGrid from '../CampaignLaunchWorkflow/ReviewGrid';
import useReviewGridEdits from '../CampaignLaunchWorkflow/useReviewGridEdits';
import { gadsGroupKeys } from '../CampaignLaunchWorkflow/gads/gadsConstants';
import { gadsResultSubtype } from '../CampaignLaunchWorkflow/gads/gadsRowBuilders';
import { AgentSidebar } from '../CampaignLaunchWorkflow/agent/AgentSidebar';
import { BulkEditPanel } from '../CampaignLaunchWorkflow/agent/BulkEditPanel';
import { buildBulkDigest, rowIdSet, serializeListFields } from '../CampaignLaunchWorkflow/agent/bulkDigest';
import { validateStaging } from '../CampaignLaunchWorkflow/agent/bulkOps';
import { useBulkEditorSession } from '../../hooks/useBulkEditorSession';
import { previewReadyToShow } from '../CampaignLaunchWorkflow/agent/previewGate';
import '../CampaignLaunchWorkflow/CampaignLaunchWorkflow.css';
import {
  copyResultFamily, buildCopyReviewRows, applyCopyReviewEdits,
  removeTargetingOption, removeTargetingType, removeCopyRow, removeCopyRows,
  DV360_TARGETING_FIELD_TYPES, DV360_SINGLETON_TARGETING_TYPES,
} from './copyReviewEdits';
import {
  copyEditableFor, copyGadsGroupCols, copyDspGroupCols, copyDspGroupKeys,
} from './copyGridConstants';
import { copyStepLabels, COPY_STEP_ADJUST_LABEL, runTtdCopyLookup } from './copyWorkflowApi';
import CopyStepper from './CopyStepper';
import CopyUrlTrackingPanel from './CopyUrlTrackingPanel';
import { propagateCampaignSuffixEdit } from './copyUrlPreview';

// ── Manual cell editing is OFF in the copy flow ──────────────────────
// CellDetailPanel shows its editor only for fields present in `editableFields`
// (see its own doc comment: "The editor only shows for fields in
// editableFields"), so handing the grid an empty set makes every cell
// read-only without touching anything else on the surface.
//
// Scoped on purpose. This set goes to the GRID alone — not to `bulkDigest.
// editableFields`, which still carries the real field list to the bulk-edit
// agent, and not to `alreadyLaunched`, which gates row removal (ReviewGrid
// L526). So the AI bulk editor, per-item list add/remove and row removal all
// keep working, which is what still lets a char_cap_exceeded or
// unconfirmed_rate_claim gate be cleared — both BLOCK the push (CopyPushStep
// L82), and the gates rail's quick-fix only covers duplicate_name.
//
// Module-level so the identity is stable across renders (a fresh `new Set()`
// in JSX would re-render the grid subtree every time).
//
// Copy-flow only: this is a prop passed from this file. CLW, launched-campaign
// and imported-campaign grids build their own editableFields and are untouched.
const COPY_GRID_NO_EDITABLE_CELLS = new Set();

/**
 * HIDDEN, NOT REMOVED — the copy grid's bulk-editor agent ("Sidekick agent").
 *
 * Flip to true to bring back the toolbar button and the sidebar it opens; the
 * session hook, digest, preview/confirm panel and undo below are all still
 * wired, so nothing else has to change. Same convention as
 * copyWorkflowApi.js's SHOW_COPY_INTERPRET_UI.
 */
const SHOW_COPY_BULK_AGENT = false;

// ── DV360 targeting removal panel ────────────────────────────────────
// DV360 targeting is editable BY REMOVAL only (pruning targeting_plan entries
// is deterministic; adding needs DV360 option-id lookups — out of scope, and
// COPY_DV360_LIST_FIELDS states that reason in the panel). The cell detail
// panel hosts per-item × buttons for the fields its list config names, but it
// is CLW's component (off-limits) and has no removal control for
// devices/environment/viewability/brandSafety and no remove-all anywhere — so
// this copy-folder panel renders in ReviewGrid's `summaryBar` slot (below the
// grid, never colliding with the slide-over) whenever an opened cell is one of
// the targeting-derived fields.
// Fields whose entries CLW's CellDetailPanel ALREADY lists with its own per-item
// × buttons (they route through the same appliers). Repeating those chips down
// here was pure duplication — the strip keeps only what the panel lacks for
// them: the remove-all action and the footnote.
const PANEL_NATIVE_REMOVAL_FIELDS = new Set([
  'geo', 'geoInclude', 'geoExclude', 'audiences', 'interests', 'keywords',
  // pmp joined the panel-native set once COPY_DV360_LIST_FIELDS granted it a
  // remove op (the panel only renders × for list fields its config names).
  'pmp',
]);

function Dv360TargetingPanel({ row, field, colLabel, onRemoveEntry, onRemoveAll }) {
  const entries = row?.targetingEntries?.[field] || [];
  if (!entries.length) return null;
  const types = DV360_TARGETING_FIELD_TYPES[field] || [];
  const hasSingleton = types.some((t) => DV360_SINGLETON_TARGETING_TYPES.has(t));
  const panelListsEntries = PANEL_NATIVE_REMOVAL_FIELDS.has(field);
  return (
    <div className="copy-workflow" style={{ display: 'block', height: 'auto', overflow: 'visible' }}>
      <div className="cw-tgt-panel">
        <div className="cw-tgt-head">
          <b>Remove {colLabel || field} targeting</b>
          <span className="cw-tgt-sub">{row.name}</span>
          <div className="gap" />
          <button
            type="button"
            className="cw-btn cw-btn-secondary cw-btn-sm"
            onClick={() => onRemoveAll(field, entries)}
          >
            Remove all{field === 'audiences' ? ' audience targeting (incl. interests)' : ''}
          </button>
        </div>
        {!panelListsEntries && (
        <div className="cw-tgt-chips">
          {entries.map((e) => (
            <span key={`${e.targetingType}:${e.optionKey}:${e.excluded}`} className="cw-tgt-chip">
              {e.excluded ? 'excl. ' : ''}{e.label}
              <button
                type="button"
                title="Remove this targeting entry"
                onClick={() => onRemoveEntry(e)}
              >×</button>
            </span>
          ))}
        </div>
        )}
        <div className="cw-tgt-foot">
          {panelListsEntries ? 'Use the × next to an item in the panel to remove just that one. ' : ''}
          {hasSingleton
            ? 'Careful: DV360 always sets this type of targeting on a new line item, so removing it here leaves the DV360 default in place rather than no targeting at all. '
            : 'Removals save right away and apply when you launch. '}
          You can remove targeting here, but not add it — add new targeting in DV360 after launch.
        </div>
      </div>
    </div>
  );
}

// ── Per-build gates card ─────────────────────────────────────────────
// Backend copy_gates.py rows for ONE build, post-resolution. Reuses the
// gate/gate-block/gate-need/gate-fix classes the push step's rail already
// styles in copy-workflow.css.
function BuildGates({ gates, campaignId, onApplyUniqueName }) {
  const blocked = gates?.blocked || [];
  const needs = gates?.needs_decision || [];
  if (blocked.length === 0 && needs.length === 0) return null;
  return (
    <div className="copy-workflow" style={{ display: 'block', height: 'auto', overflow: 'visible' }}>
      <div className="gate-banner cw-buildfail">
        <span className="material-icons">{blocked.length ? 'block' : 'help'}</span>
        <div className="t">
          {blocked.map((b) => (
            <div className="gate-fix" key={`${b.code}-${b.field}`}>
              <div className="kv"><span>{b.code}</span><b>{b.message}</b></div>
              {/* A duplicate name is the one blocker with an unambiguous
                  automatic fix — the rename goes through the same
                  applier + planning-review save a manual grid edit takes. */}
              {b.code === 'duplicate_name' && onApplyUniqueName && (
                <button
                  type="button"
                  className="cw-btn cw-btn-secondary cw-btn-sm"
                  onClick={() => onApplyUniqueName(campaignId)}
                >
                  Use a unique name
                </button>
              )}
            </div>
          ))}
          {needs.map((n) => (
            <div className="gate-fix" key={`${n.code}-${n.field}`}>
              <div className="kv"><span>{n.code}</span><b>{n.message}</b></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── One build's review grid ──────────────────────────────────────────
// Owns the per-grid view state (group tab, search, detail panel, in-flight
// cell edit) exactly the way CLW's review pane does; the DATA round-trips
// through the container (edit → applyCopyReviewEdits → onSaveBuild → new
// launcherResult prop → rows rebuild), so there is no local edited-rows
// layer that could diverge from what was persisted.
function CopyBuildGrid({
  campaign, build, onSaveBuild, campaignName, accountId, userId, readOnly = false,
  aiOpen, setAiOpen, startAiResize,
}) {
  // WHY NOT build.alreadyLaunched:
  //
  // That flag comes from GET /planning-review, where it means
  // `jsonResponse.status !== 'review'` — i.e. "this orchestration is no longer
  // awaiting review". The copy workflow treats BOTH 'review' AND 'completed' as
  // build-ready (isBuildReady in copyWorkflowApi.js), so a copy whose build
  // orchestration finished reports alreadyLaunched === true while sitting on the
  // Adjust step with "Continue to push" still unpressed. The grid then locked
  // itself and the bulk editor refused every request with "it already launched"
  // — on a draft that had never been pushed.
  //
  // The Adjust step precedes the push by construction, so the honest signal is
  // the copy's OWN push state, which the container already tracks and renders as
  // the "Draft — not pushed" / "Pushed" badge.
  const launcherResult = build.launcherResult;
  const family = copyResultFamily(launcherResult);
  const gadsSubtype = family === 'gads' ? gadsResultSubtype(launcherResult) : null;

  const reviewRows = useMemo(() => buildCopyReviewRows(launcherResult), [launcherResult]);
  const { editableFields, fieldEditors, listFields } = copyEditableFor(family, gadsSubtype);
  const groupCols = family === 'gads' ? copyGadsGroupCols(gadsSubtype) : copyDspGroupCols();
  const groupKeys = family === 'gads' ? gadsGroupKeys(gadsSubtype) : copyDspGroupKeys();

  const [activeGroup, setActiveGroup] = useState(groupKeys[0]);
  const [gridSearch, setGridSearch] = useState('');
  const [filterIssuesOnly, setFilterIssuesOnly] = useState(false);
  const [detailRowId, setDetailRowId] = useState(null);
  const [detailCellField, setDetailCellField] = useState(null);
  // Panel open state and width live in CopyPlanStep, not here: the panel is
  // viewport-fixed, so two open at once would sit on top of each other. One
  // build owns the panel at a time (see openAgentFor there).

  // Shared edit-state hook in IMMEDIATE mode: one save per commit — apply the
  // single edit onto a clone of the WHOLE launcherResult and hand it up for
  // the PUT. Alignment note: this is the versioned-saves model CLW uses
  // server-side, minus its client batching — acceptable per the copy
  // workflow's contract.
  //
  // List operations (remove / add / edit) apply-and-save through the same
  // path, staged one at a time by CellDetailPanel's EditableList. Three
  // sources feed them: Google Ads keyword / copy / geo / theme lists
  // (gadsEdits, which speaks the full op shape), DV360 targeting-plan entries
  // via the row's targetingEntries (remove-only — the panel's × buttons hand
  // back exactly the labels the derivation listed), and TTD, where every list
  // rides the source's bid lists by reference and so has no write target at
  // all. An op the family's applier can't honour leaves the template
  // byte-identical, and the guard below skips the PUT rather than showing a
  // "Saving your edit…" banner for a change that didn't happen.
  const saveIfChanged = (edited) => {
    if (JSON.stringify(edited) === JSON.stringify(launcherResult)) return;
    onSaveBuild(campaign.id, edited);
  };
  const {
    editingCell, setEditingCell, editingDraft, setEditingDraft,
    commitCellEdit, cancelCellEdit, toggleListDeletion, addListItem, editListItem,
  } = useReviewGridEdits({
    mode: 'immediate',
    onCommitEdit: (rowId, field, value) => {
      const edited = applyCopyReviewEdits(
        launcherResult,
        { [rowId]: { [field]: value } },
        {},
        reviewRows,
      );
      onSaveBuild(campaign.id, edited);
    },
    onListOp: (rowId, field, op) => {
      saveIfChanged(applyCopyReviewEdits(launcherResult, {}, { [rowId]: { [field]: [op] } }, reviewRows));
    },
  });

  // Sitelink edits from the URL panel take the SAME path as every other list op —
  // one applyCopyReviewEdits, one save. They arrive keyed by campaign index
  // because sitelinks are campaign-level (verified account-wide: zero exist at
  // asset-group, ad-group or account level), while the edit layer is keyed by grid
  // rowId — so pick any row belonging to that campaign. gadsEdits then writes the
  // campaign's own list, since a PMax asset group has no sitelinks of its own.
  const handleSitelinkOp = (campaignIndex, op) => {
    const row = reviewRows.find((r) => String(r.rowId || '').startsWith(`gads-${campaignIndex}-`));
    if (!row) return;
    saveIfChanged(applyCopyReviewEdits(
      launcherResult, {}, { [row.rowId]: { sitelinks: [op] } }, reviewRows,
    ));
  };

  // Campaign-level URL tracking, edited in the same panel. A scalar edit rather
  // than a list op, so it takes applyCopyReviewEdits' `edits` path — and an empty
  // value is a real instruction here ("this copy carries no tracking template"),
  // which is why the writer deletes the key rather than skipping a blank.
  // A campaign-level suffix edit has to reach the SITELINKS too, and that is not
  // cosmetic: on the Google side an asset's own final URL suffix OVERRIDES the
  // campaign's rather than adding to it, so a sitelink carrying its own never sees
  // a campaign-level change. Editing the campaign suffix and watching six tagged
  // sitelinks keep the old values is the bug this fixes — the ads picked the edit
  // up, the sitelinks silently did not.
  //
  // ONLY THE KEYS THAT CHANGED are pushed down. Propagating everything the campaign
  // states looks equivalent and is not: these suffixes state utm_content themselves
  // (utm_content={creative}), so an edit to utm_source dragged that down and replaced
  // every per-link label. See propagateCampaignSuffixEdit.
  //
  // Only sitelinks that HAVE their own suffix are touched: a blank one already
  // inherits the campaign's, so writing it would convert an inheriting sitelink
  // into a pinned one and silently opt it out of the next campaign-level edit.
  //
  // Both halves go through ONE applyCopyReviewEdits and ONE save, keeping the
  // one-save-per-commit contract the rest of this surface holds to.
  const handleCampaignUrlEdit = (campaignIndex, fields) => {
    const row = reviewRows.find((r) => String(r.rowId || '').startsWith(`gads-${campaignIndex}-`));
    if (!row) return;

    const campaign = (launcherResult?.campaigns || [])[campaignIndex];
    const prevSuffix = String(campaign?.final_url_suffix || '');
    const suffixChanged = Object.prototype.hasOwnProperty.call(fields, 'finalUrlSuffix')
      && String(fields.finalUrlSuffix || '') !== prevSuffix;
    const ops = (suffixChanged ? (campaign?.sitelinks || []) : [])
      .map((sl) => {
        const own = sl && typeof sl === 'object' ? sl.final_url_suffix : null;
        if (!own || !String(own).trim()) return null;
        const merged = propagateCampaignSuffixEdit(prevSuffix, fields.finalUrlSuffix, own);
        if (merged === String(own)) return null;
        return { op: 'edit', value: sl.link_text, fields: { final_url_suffix: merged } };
      })
      .filter(Boolean);

    saveIfChanged(applyCopyReviewEdits(
      launcherResult,
      { [row.rowId]: fields },
      ops.length ? { [row.rowId]: { sitelinks: ops } } : {},
      reviewRows,
    ));
  };

  // ── Bulk editor agent ───────────────────────────────────────────────
  // The Adjust grid's first agent. It fits this surface more simply than CLW's:
  // immediate mode has no pending layer, so a confirmed batch is ONE
  // applyCopyReviewEdits + ONE save — which is exactly the one-save-per-commit
  // contract this flow already keeps, just with a bigger commit.
  const bulkDigest = useMemo(
    () => buildBulkDigest(reviewRows, editableFields),
    [reviewRows, editableFields],
  );
  // ── Row selection — the agent's scope ────────────────────────────────────
  // Ticked rows narrow every edit the bulk editor proposes. Pruned whenever the
  // rows are rebuilt: Google Ads copy rowIds are POSITIONAL, so a selection
  // carried across a save that removed a row would re-point at a different one.
  const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());
  useEffect(() => {
    setSelectedRowIds((prev) => {
      if (prev.size === 0) return prev;
      const live = rowIdSet(reviewRows);
      const next = new Set([...prev].filter((id) => live.has(String(id))));
      return next.size === prev.size ? prev : next;
    });
  }, [reviewRows]);
  const selectedRowIdList = useMemo(() => [...selectedRowIds], [selectedRowIds]);

  const bulkCreatePayload = useMemo(() => (
    family && campaign?.id && bulkDigest.rows.length
      ? {
          // copyResultFamily already speaks the platform vocabulary the agent
          // uses ('ttd' | 'dv360' | 'gads').
          platform: family,
          surface: 'copy',
          // The NEW copy's own row, not the source campaign's. Keyed on the
          // source, every copy run from the same source RESUMED one session and
          // inherited its conversation and stale digest — so a fresh copy request
          // is now a fresh session by construction. Falls back to the source id
          // only if the build has not reported its row yet.
          campaignId: build.campaignRowId || campaign.id,
          campaignName: campaignName || null,
          rows: bulkDigest.rows,
          editableFields: bulkDigest.editableFields,
          // List-op capabilities — a field can grant add/remove while having no
          // scalar writer (Copy TTD geo), so the agent needs both lists.
          listFields: serializeListFields(listFields),
          rowLabel: family === 'dv360' ? 'line items' : 'ad groups',
          // The user's ticked rows. A HARD scope on every edit the agent
          // proposes — enforced in services/bulkEdit/selector.js.
          selectedRowIds: selectedRowIdList,
          // Push state deliberately not sent — bulk editing is available in every
          // state, including after a push. See bulkOps.js validateStaging. The
          // manual cell editor still respects `readOnly`.
          launcherResult,
        }
      : null
  ), [
    family, campaign, build.campaignRowId, campaignName, bulkDigest, launcherResult,
    selectedRowIdList, listFields,
  ]);
  const bulkAgent = useBulkEditorSession({
    accountId,
    userId,
    autoStart: aiOpen && Boolean(bulkCreatePayload),
    createPayload: bulkCreatePayload || {},
  });

  const stagedBatchTs = useRef(null);
  const [bulkStageNote, setBulkStageNote] = useState(null);
  useEffect(() => {
    const batch = bulkAgent.lastStagedBatch;
    if (!batch || batch.ts === stagedBatchTs.current) return;
    stagedBatchTs.current = batch.ts;
    const { edits, listOps, rowOps, refused, accepted } = validateStaging(batch.staging, {
      editableFields,
      listFields: serializeListFields(listFields),
      knownRowIds: rowIdSet(reviewRows),
    });
    if (accepted) {
      // One apply, one save — the whole batch lands atomically rather than
      // firing a PUT per row.
      saveIfChanged(applyCopyReviewEdits(launcherResult, edits, listOps, reviewRows, rowOps));
      // Arm undo. This grid has always applied-and-saved on confirm, so the thing
      // it was missing was the way back out: snapshot the template as it was
      // immediately before the batch. Every save here is a whole-document PUT, so
      // undo is just re-saving this — no inverse-operation logic, and it covers
      // scalar edits, list ops and row deletions alike.
      setUndoEntry({
        snapshot: launcherResult,
        label: batch.summary || `${accepted} agent change${accepted === 1 ? '' : 's'}`,
        count: accepted,
        ts: batch.ts,
      });
    }
    setBulkStageNote({
      accepted,
      refused,
      text: accepted
        ? `Applied ${accepted} change${accepted === 1 ? '' : 's'} to this copy.`
        : 'Nothing was applied.',
      ts: batch.ts,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkAgent.lastStagedBatch]);

  // One-step undo for the last agent batch. Rides the same container save as
  // every other edit, so the undo is itself persisted and itself reversible.
  const [undoEntry, setUndoEntry] = useState(null);
  const revertUndo = () => {
    if (!undoEntry) return;
    onSaveBuild(campaign.id, undoEntry.snapshot);
    setUndoEntry(null);
    // The open cell panel may point at a row the revert removes — Google Ads copy
    // rowIds are positional, so a stale id would address a different entity.
    setDetailRowId(null);
    setDetailCellField(null);
  };

  // Re-seed the agent's rows after every save — the container hands down a new
  // launcherResult, so reviewRows (and any row id derived positionally) change
  // under the agent. Debounced so a burst of saves sends one refresh.
  useEffect(() => {
    if (!bulkAgent.sessionId || !bulkDigest.rows.length) return;
    const t = setTimeout(() => {
      bulkAgent.refreshDigest({
        rows: bulkDigest.rows,
        editableFields: bulkDigest.editableFields,
        // Ticking a row re-scopes the agent through this refresh — no separate
        // call, and no agent turn spent on it.
        selectedRowIds: selectedRowIdList,
        launcherResult,
      });
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkAgent.sessionId, bulkDigest, selectedRowIdList]);

  // Rows always reflect the SAVED launcherResult (no pending-edit overlay —
  // commits save immediately), so getRowValue is a plain read.
  const getRowValue = (row, key) => row[key];

  // Drop a child from this copy. This is the exclusion capability the 3-step
  // flow promised when it removed the picker step ("excluding children is a row
  // DELETION in the Adjust grid") and never implemented, so until now a copy
  // shipped every child of its source. It edits the built template and rides
  // the same planning-review PUT as every other edit — no new endpoint.
  //
  // removeCopyRow refuses to strand a parent with no children and returns the
  // ORIGINAL object when it declines, so the identity check both skips the save
  // and tells us to surface the reason.
  const [rowRemovalError, setRowRemovalError] = useState(null);
  const removeRow = (rowId) => {
    const { launcherResult: edited, removed, reason } = removeCopyRow(launcherResult, rowId);
    if (!removed) {
      setRowRemovalError(reason);
      return;
    }
    setRowRemovalError(null);
    // The open cell panel may point at the row that just went away — Google Ads
    // rowIds are positional, so a stale id would address a different entity.
    setDetailRowId(null);
    setDetailCellField(null);
    onSaveBuild(campaign.id, edited);
  };

  // Same drop, for every checked row at once.
  //
  // ONE template edit and ONE save, not N: each removeCopyRow call folds into
  // the result of the last, so the PUT that lands carries every removal. Doing
  // it per row would fire N saves whose last writer wins — and on Google Ads,
  // where rowIds are POSITIONAL, the second save would address rows that the
  // first one had already shifted.
  //
  // A refusal (the last child of its parent) skips that row and keeps the
  // rest: a bulk action that aborts wholesale on one bad row makes the user
  // hunt for which one it was.
  //
  // removeCopyRows, not a loop here: on DV360 a selection that covers a whole
  // insertion order drops the ORDER too, which is what the grid's sibling-group
  // prompt exists to help the user select — see its docstring.
  const removeRows = (rowIds) => {
    const { launcherResult: next, removedCount, reasons } = removeCopyRows(launcherResult, rowIds);
    if (removedCount === 0) {
      setRowRemovalError(reasons[0] || 'Nothing to remove');
      return;
    }
    setRowRemovalError(reasons.length > 0
      ? `Removed ${removedCount} of ${rowIds.length}. ${reasons.join(' ')}`
      : null);
    setDetailRowId(null);
    setDetailCellField(null);
    // The selection now names rows that no longer exist.
    setSelectedRowIds(new Set());
    onSaveBuild(campaign.id, next);
  };

  const visibleRows = reviewRows.filter((r) => {
    if (filterIssuesOnly && Object.keys(r.annotations || {}).length === 0) return false;
    if (!gridSearch.trim()) return true;
    return String(r.name || '').toLowerCase().includes(gridSearch.trim().toLowerCase());
  });

  const detailRow = reviewRows.find((r) => r.rowId === detailRowId) || null;
  const effectiveActiveGroup = groupCols[activeGroup] ? activeGroup : groupKeys[0];
  const cols = groupCols[effectiveActiveGroup] || [];

  // The removal panel's own paths (fields the CLW detail panel has no ×
  // buttons for, and remove-all). `interests` remove-all iterates entries —
  // it shares TARGETING_TYPE_AUDIENCE_GROUP with audiences, so dropping the
  // whole type from an interests context would silently nuke audiences too.
  const removeTargetingEntry = (rowId, entry) => {
    onSaveBuild(campaign.id, removeTargetingOption(
      launcherResult, rowId, entry.targetingType, entry.optionKey,
    ));
  };
  const removeTargetingAll = (rowId, field, entries) => {
    let edited;
    if (field === 'interests') {
      edited = entries.reduce(
        (lr, e) => removeTargetingOption(lr, rowId, e.targetingType, e.optionKey),
        launcherResult,
      );
    } else {
      edited = (DV360_TARGETING_FIELD_TYPES[field] || []).reduce(
        (lr, type) => removeTargetingType(lr, rowId, type),
        launcherResult,
      );
    }
    onSaveBuild(campaign.id, edited);
  };

  // The opened cell, when it is one of the targeting-derived fields with
  // removable entries — drives the removal panel below the grid.
  const targetingField = family === 'dv360' && detailRow && detailCellField
    && DV360_TARGETING_FIELD_TYPES[detailCellField]
    && detailRow.targetingEntries?.[detailCellField]?.length
    ? detailCellField : null;
  const targetingColLabel = targetingField
    ? (cols.find((c) => c.k === targetingField)?.l
      || Object.values(groupCols).flat().find((c) => c.k === targetingField)?.l
      || targetingField)
    : null;

  // Build warnings, deduped: a merged copy (one shell -> N platform campaigns)
  // concatenates each sub-build's warnings, and a per-line-item message repeats
  // verbatim across them. Strings only — anything else is ignored rather than
  // rendered as "[object Object]".
  const copyWarnings = Array.from(new Set(
    (Array.isArray(launcherResult?.copy_warnings) ? launcherResult.copy_warnings : [])
      .filter((w) => typeof w === 'string' && w.trim())
      .map((w) => w.trim()),
  ));

  // A few plain bullets over the top of those warnings, written during the
  // BUILD (campaign_editor/copy_warning_summary.py) so it is already there when
  // this step loads. Same validation as the raw list, because it arrives from
  // the same place and one of its two producers is a language model.
  //
  // Absent — an older build, too few warnings to be worth condensing, or a
  // summarizer that failed — and the raw list renders exactly as it did
  // before. The summary NEVER replaces the detail: the full list stays one
  // click below it, because a bullet saying "22 line items lost audience
  // targeting" is not an answer to "which ones".
  const warningSummary = (launcherResult?.copy_warnings_summary?.bullets || [])
    .filter((b) => typeof b === 'string' && b.trim())
    .map((b) => b.trim());

  return (
    // has-dpanel mirrors what CampaignLaunchWorkflow puts on `.clw-pane.review`;
    // copy-workflow.css reproduces the matching rules against `.clw` because
    // this workflow has no such pane. Without it the open cell-detail panel
    // overlaps the grid instead of shrinking it.
    <div className={`clw${detailRow ? ' has-dpanel' : ''}${aiOpen ? ' has-aipanel' : ''}`}>
      {/* What the BUILD dropped or rewrote (launcherResult.copy_warnings, put
          there by campaign_copy_handler._persist_launcher_payload). This has to
          be on the grid, next to the cells it explains: a copy legitimately
          drops targeting it cannot carry — a DV360 cross-advertiser copy drops
          every advertiser-scoped type (audiences, keywords, inventory
          sources/PMP, POI) because those ids only resolve under the source
          advertiser, and a TTD copy drops bid lists the new ad group is not
          authorized to reference. The builder has always NAMED each one, but the
          warnings stopped at the campaign-copy result file in GCS, so the review
          grid showed '—' with no reason and the drop read as a bug. Silent
          degradation is the thing to prevent; the cells alone can't say it. */}
      {/* WHERE THE CLICKS LAND. Above the warnings on purpose: the warnings say
          what could not be carried, this says what WILL happen — and a copy whose
          URLs still report as the source campaign is the one failure the grid
          itself cannot show, because every cell looks right. Renders nothing
          when the build produced no plan (a non-Google copy, or an older build). */}
      <CopyUrlTrackingPanel
        plan={launcherResult?.url_tracking_plan}
        launcherResult={launcherResult}
        onSitelinkOp={family === 'gads' ? handleSitelinkOp : undefined}
        onCampaignUrlEdit={family === 'gads' ? handleCampaignUrlEdit : undefined}
      />
      {copyWarnings.length > 0 && (
        <div className="copy-workflow" style={{ display: 'block', height: 'auto', overflow: 'visible' }}>
          <div className="callout cw-building" aria-live="polite">
            <span className="material-icons">warning</span>
            <div>
              <b>
                {copyWarnings.length} thing{copyWarnings.length === 1 ? '' : 's'} could not
                be copied
              </b>
              {' — set them up on the copy after you launch it.'}
              {warningSummary.length > 0 ? (
                <>
                  <ul className="cw-warnlist">
                    {warningSummary.map((b) => <li key={b}>{b}</li>)}
                  </ul>
                  {/* Details on demand. Closed by default — the point of the
                      summary is not having to read 22 near-identical lines —
                      but one click away, because "which line items?" is a fair
                      question the bullets cannot answer. */}
                  <details className="cw-warnall">
                    <summary>
                      Show all {copyWarnings.length} warning{copyWarnings.length === 1 ? '' : 's'}
                    </summary>
                    <ul className="cw-warnlist">
                      {copyWarnings.map((w) => <li key={w}>{w}</li>)}
                    </ul>
                  </details>
                </>
              ) : (
                <ul className="cw-warnlist">
                  {copyWarnings.map((w) => <li key={w}>{w}</li>)}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
      {/* The standing "how DV360 targeting works on a copy" note that used to
          sit here is gone. What it had to say about a SPECIFIC cell — remove
          entries here, adding happens in DV360 — is said by the removal panel
          that opens with that cell (Dv360TargetingPanel), where it is about
          something the user is looking at rather than a paragraph to read past
          on the way to the grid. */}
      {rowRemovalError && (
        <div className="copy-workflow" style={{ display: 'block', height: 'auto', overflow: 'visible' }}>
          <div className="gate-banner cw-buildfail" aria-live="polite">
            <span className="material-icons">error</span>
            <div className="t"><b>Can&apos;t remove that row:</b> {rowRemovalError}</div>
          </div>
        </div>
      )}
      {(build.saving || build.saveError) && (
        <div className="copy-workflow" style={{ display: 'block', height: 'auto', overflow: 'visible' }}>
          {build.saving && (
            <div className="callout" aria-live="polite">
              <span className="material-icons cw-spin">autorenew</span>
              <div>Saving your edit…</div>
            </div>
          )}
          {!build.saving && build.saveError && (
            <div className="gate-banner cw-buildfail">
              <span className="material-icons">error</span>
              <div className="t">
                <b>Couldn&apos;t save:</b> {build.saveError} Your edits are still here — change any
                cell to try again.
              </div>
            </div>
          )}
        </div>
      )}
      <ReviewGrid
        reviewRows={reviewRows}
        visibleRows={visibleRows}
        getRowValue={getRowValue}
        editedRows={{}}
        groupCols={groupCols}
        groupKeys={groupKeys}
        cols={cols}
        effectiveActiveGroup={effectiveActiveGroup}
        isGt={false}
        activeGroup={activeGroup}
        setActiveGroup={setActiveGroup}
        gridSearch={gridSearch}
        setGridSearch={setGridSearch}
        filterIssuesOnly={filterIssuesOnly}
        setFilterIssuesOnly={setFilterIssuesOnly}
        detailRow={detailRow}
        detailCellField={detailCellField}
        setDetailRowId={setDetailRowId}
        setDetailCellField={setDetailCellField}
        editingCell={editingCell}
        setEditingCell={setEditingCell}
        editingDraft={editingDraft}
        setEditingDraft={setEditingDraft}
        commitCellEdit={commitCellEdit}
        cancelCellEdit={cancelCellEdit}
        editableFields={COPY_GRID_NO_EDITABLE_CELLS}
        fieldEditors={fieldEditors}
        ctsReview={null}
        pendingDeletions={{}}
        toggleListDeletion={toggleListDeletion}
        // Per-family list capabilities (copyGridConstants) + the add/edit
        // callbacks. Immediate mode stages nothing, so pendingListOps stays
        // empty: each op applies and saves as it is committed.
        listFields={listFields}
        pendingListOps={{}}
        addListItem={addListItem}
        editListItem={editListItem}
        alreadyLaunched={readOnly}
        // Row multi-select — scopes every edit the bulk editor proposes.
        selectedRowIds={selectedRowIds}
        onSelectionChange={setSelectedRowIds}
        // Bulk drop of every checked row — one template edit, one save.
        onRemoveRows={removeRows}
        // DV360 only: checking one line item asks whether to take the other
        // line items in its insertion order too. Grouped on ioGroupKey (an
        // exact per-IO identity from the row builder), NOT the display name —
        // two orders can be named the same. Other families pass nothing, so
        // their grids prompt for nothing.
        {...(family === 'dv360' ? {
          siblingGroupField: 'ioGroupKey',
          siblingGroupLabelField: 'ioName',
          siblingGroupNoun: 'insertion order',
          siblingGroupItemNoun: 'line item',
          siblingGroupNote: 'Removing every line item in an order drops the ORDER too — it '
            + 'is never created. Only the copy’s last remaining order can’t go, since a '
            + 'campaign with nothing under it can’t launch.',
        } : {})}
        // Toolbar affordances this surface doesn't want. CLW passes none of
        // these, so its own grid keeps all three.
        //   • search — the copy grid shows one campaign's own children, not a
        //     media plan's placements; there is nothing to hunt through.
        //   • issues-only — same reason the count tiles went: the rows ARE the
        //     issue list here, and the gates card above states what blocks.
        //   • the agent button, with the sidebar below (see SHOW_COPY_BULK_AGENT).
        showSearch={false}
        showIssuesFilter={false}
        showAgentButton={SHOW_COPY_BULK_AGENT}
        // Row removal = drop this child from the copy (see removeRow above).
        // Lookup-backed list adds (TTD geo segments / 3P audiences). Without
        // this the panel keeps `add` disabled for any field whose config names
        // a lookup — which is why TTD geo looked read-only even once its config
        // granted add.
        onLookup={(kind, query) => runTtdCopyLookup(campaign.id, kind, query, { accountId, userId })}
        onRemoveRow={removeRow}
        removeRowTitle={
          family === 'dv360' ? 'Remove this line item from the copy'
            : family === 'gads' ? 'Remove this row from the copy'
              : 'Remove this ad group from the copy'
        }
        campaignName={campaignName}
        aiOpen={aiOpen}
        setAiOpen={setAiOpen}
        widthsKey={`copy-plan-grid-widths-${family || 'unknown'}`}
        // Google Ads copy grids put the CAMPAIGN on the sticky name column (see
        // overlayGadsCopyRows), so label the two sticky columns Order / Campaign;
        // other families keep the generic defaults (unlabeled index / "Name").
        {...(family === 'gads' ? { stickyF1Label: 'Order', stickyF2Label: 'Campaign' } : {})}
        // Renders BELOW the grid (never colliding with the cell-detail
        // slide-over) whenever the opened cell is a targeting-derived field —
        // see Dv360TargetingPanel's own comment for why this lives here
        // instead of inside CLW's panel.
        summaryBar={targetingField ? (
          <Dv360TargetingPanel
            row={detailRow}
            field={targetingField}
            colLabel={targetingColLabel}
            onRemoveEntry={(entry) => removeTargetingEntry(detailRow.rowId, entry)}
            onRemoveAll={(field, entries) => removeTargetingAll(detailRow.rowId, field, entries)}
          />
        ) : null}
      >
        {/* The Adjust grid's agent — HIDDEN behind SHOW_COPY_BULK_AGENT.
            Everything it needs is still built above; only the render is gated,
            so turning the flag on restores the sidebar as it was. Single-mode
            (no reviewer agent exists for copy builds), so AgentSidebar renders
            no mode switcher. */}
        {SHOW_COPY_BULK_AGENT ? (
          <AgentSidebar
          open={aiOpen}
          setOpen={setAiOpen}
          onResizeStart={startAiResize}
          session={bulkCreatePayload ? bulkAgent : null}
          feedItems={[
            // Each notice carries the timestamp of the thing it reports, so the sidebar
            // can interleave it with the conversation instead of pinning it under
            // everything. See agent/feedOrder.js.
            // `pin: 'end'` keeps the card BELOW the agent's explanation, and
            // previewReadyToShow holds it back until that explanation exists — a
            // preview lands on the tool result, seconds before the agent finishes
            // describing it, so by timestamp alone the Confirm button arrived above
            // the text saying what you would be confirming.
            previewReadyToShow(bulkAgent) && {
              key: `preview-${bulkAgent.activePreview.preview_id}`,
              pin: 'end',
              node: (
                <BulkEditPanel
                  preview={bulkAgent.activePreview}
                  onConfirm={(id) => bulkAgent.approvePreview(id, 'click')}
                  // Mark it dismissed (which also takes the card down) before telling the
                  // agent — the message alone left the card up with a live Confirm.
                  onDismiss={(id) => {
                    bulkAgent.dismissPreview?.(id);
                    bulkAgent.sendMessage(`I'm not applying preview ${id}. Don't stage it.`);
                  }}
                  approving={bulkAgent.approving}
                  approveError={bulkAgent.approveError}
              // This grid is immediate-mode: confirming applies AND saves
              // in one step, with no Save button to point the user at.
              savesImmediately
                />
              ),
            },
            bulkStageNote && {
              key: `staged-${bulkStageNote.ts}`,
              ts: bulkStageNote.ts,
              node: (
                <div className={`clw-bulk-result ${bulkStageNote.accepted ? 'ok' : 'warn'}`}>
                  <span>
                    {bulkStageNote.text}
                    {bulkStageNote.accepted && build.saving && <> Saving…</>}
                    {bulkStageNote.accepted && !build.saving && !build.saveError && <> Saved.</>}
                    {bulkStageNote.refused?.length > 0 && (
                      <> Skipped: {bulkStageNote.refused.map((r) => r.reason).join(' ')}</>
                    )}
                  </span>
                  {/* Sequence: confirm → the batch applies and saves → THEN this
                      appears. Held back while the save is in flight, and withheld
                      entirely if it failed, because on a failure there is nothing
                      persisted to undo. */}
                  {undoEntry && undoEntry.ts >= bulkStageNote.ts
                    && !build.saving && !build.saveError && (
                    <span className="clw-bulk-undo">
                      <button type="button" onClick={revertUndo}>Undo</button>
                      <button type="button" className="dismiss" onClick={() => setUndoEntry(null)} title="Keep the change">
                        ✕
                      </button>
                    </span>
                  )}
                </div>
              ),
            },
            bulkAgent.lastBulkFailure && !bulkAgent.lastBulkFailure.awaitingApproval && {
              key: `failed-${bulkAgent.lastBulkFailure.ts}`,
              ts: bulkAgent.lastBulkFailure.ts,
              node: (
                <div className="clw-bulk-result warn">
                  <span>{bulkAgent.lastBulkFailure.error}</span>
                </div>
              ),
            },
          ].filter(Boolean)}
          config={{
            title: 'Bulk editor',
            note: 'edits many rows at once',
            placeholder: {
              title: 'What do you want to change?',
              body: (
                <>I can change <strong>dates</strong>, <strong>budgets</strong> and <strong>geo</strong> across many rows of this copy at once. I&apos;ll show you exactly what changes and ask before applying anything.</>
              ),
              sectionLabel: 'Try asking',
              prompts: [
                'Move everything to a 2026-09-01 → 2026-09-30 flight.',
                'Which rows have no end date?',
                'Add Denver to the geo on every row.',
                'Cut every budget by 15%.',
              ],
            },
            autoQaMessage: null,
            loadingMessage: 'Reading this copy\u2019s rows — one moment.',
            inputPlaceholder: 'Describe the change across these rows…',
            inputPlaceholderNoSession: 'Bulk editor unavailable for this build',
            fallbackMessage: `This copy has ${reviewRows.length} row${reviewRows.length === 1 ? '' : 's'}. Tell me what to change across them.`,
          }}
          context={{ rowsCount: reviewRows.length, rowLabel: family === 'dv360' ? 'line items' : 'ad groups' }}
          scopeNote={selectedRowIds.size > 0 ? (
            <div className="clw-ai-scope">
              <span>
                Scoped to <strong>{selectedRowIds.size}</strong> selected {selectedRowIds.size === 1 ? 'row' : 'rows'} —
                edits won&apos;t touch anything else.
              </span>
              <button type="button" onClick={() => setSelectedRowIds(new Set())}>Clear</button>
            </div>
          ) : null}
          />
        ) : null}
      </ReviewGrid>
    </div>
  );
}

export default function CopyPlanStep({
  // Step rail labels — always the flow's 3 entries; defaulted so a caller that
  // doesn't pass them still renders the right rail.
  stepLabels = copyStepLabels(),
  // accountId / userId reach the cell panel's TTD segment lookup.
  sourceCampaigns, builds, gatesByCampaign, accountId, userId,
  onApplyUniqueName, onSaveBuild, onRetryBuild, campaignName, onBack, onContinue,
  // Whether this COPY has been pushed. See the readOnly note below — it is not
  // the same thing as the build's inherited `alreadyLaunched`.
  pushed = false,
}) {
  // ── Agent panel: viewport-fixed, one at a time ──────────────────────
  //
  // The panel used to be absolutely positioned inside each build's `.clw` block,
  // which lives in this step's page scroller BELOW the gates card — so it started
  // partway down the page and scrolled away with the content. It is now fixed to
  // the viewport (see copy-workflow.css), which means:
  //   • open state has to live HERE, because two fixed panels would stack on top
  //     of each other — one build owns the panel at a time;
  //   • the width var has to be set on the PANE, not the build block, so the
  //     whole scroller can be padded (the gates card and section headers sit
  //     outside `.clw-grid-wrap` and would otherwise be covered);
  //   • the panel's bottom has to clear the Back / Continue-to-push bar, whose
  //     height is measured below rather than hardcoded — it wraps to two lines at
  //     narrow widths, and a stale guess would either gap or cover the buttons.
  const [openAgentFor, setOpenAgentFor] = useState(null);
  const [aiPanelWidth, setAiPanelWidth] = useState(() => {
    try {
      const stored = Number(window.localStorage.getItem('copy-ai-panel-w'));
      return Number.isFinite(stored) && stored >= 280 ? stored : 520;
    } catch { return 520; }
  });
  const startAiResize = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = aiPanelWidth;
    document.body.classList.add('clw-col-resizing');
    const onMove = (ev) => {
      const next = Math.max(280, Math.min(900, Math.round(startWidth + (startX - ev.clientX))));
      setAiPanelWidth(next);
      try { window.localStorage.setItem('copy-ai-panel-w', String(next)); } catch { /* ignore */ }
    };
    const onUp = () => {
      document.body.classList.remove('clw-col-resizing');
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [aiPanelWidth]);

  // Measure the push bar so the fixed panel can stop exactly above it.
  const pushBarRef = useRef(null);
  const [pushBarH, setPushBarH] = useState(56);
  useEffect(() => {
    const el = pushBarRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setPushBarH(el.offsetHeight || 56));
    ro.observe(el);
    setPushBarH(el.offsetHeight || 56);
    return () => ro.disconnect();
  }, []);

  /**
   * ReviewGrid's toggle calls this as `setAiOpen(v => !v)`, so the functional
   * form has to be honoured — and opening one build's panel closes any other.
   */
  const agentOpenSetterFor = useCallback((campaignId) => (next) => {
    setOpenAgentFor((prev) => {
      const isOpen = prev === campaignId;
      const want = typeof next === 'function' ? next(isOpen) : Boolean(next);
      if (want) return campaignId;
      return isOpen ? null : prev;
    });
  }, []);

  // Every source campaign gets a build — the old picker-step excludedIds
  // pre-filter is gone (exclusion = row deletion in the grid below).
  const included = sourceCampaigns;

  const buildFor = (c) => builds?.[c.id] || { status: 'building' };
  const building = included.filter((c) => buildFor(c).status === 'building');
  const reviewed = included.filter((c) => buildFor(c).status === 'review' && buildFor(c).launcherResult);

  // Gate counts for the bottom bar. The stat-tile summary that used to head
  // this step (campaigns built / rows in plan / blocked / needs decision) is
  // gone — the grid below states the same thing in full, and the two counts
  // that actually gate the push still show in the bar with the buttons.
  const nBlocked = included.reduce((n, c) => n + (gatesByCampaign?.[c.id]?.blocked?.length || 0), 0);
  const nNeed = included.reduce((n, c) => n + (gatesByCampaign?.[c.id]?.needs_decision?.length || 0), 0);

  return (
    <div
      className={`cw-pane${openAgentFor !== null ? ' cw-has-aipanel' : ''}`}
      style={{ '--ai-panel-w': `${aiPanelWidth}px`, '--cw-pushbar-h': `${pushBarH}px` }}
    >
      {/* Everything above the pinned bottom bar scrolls as one page —
          `.cw-pane` is overflow:hidden (flex column), and CLW's own
          grid-scroll chain (`.clw-grid-wrap`/`.clw-grid-scroll`, flex:1)
          needs a BOUNDED ancestor it doesn't get here: each per-campaign
          <section> is an auto-height block, so 24 rows simply rendered past
          the pane's clip with no scrollbar anywhere. `.cw-scroll` is the
          same page-scroll container the Describe step already uses; long
          grids ADDITIONALLY scroll internally with CLW's sticky header via
          the `.clw-grid-scroll` max-height rule in copy-workflow.css, so
          stacked multi-campaign sections stay reachable either way. */}
      <div className="cw-scroll">
      <CopyStepper steps={stepLabels} current={stepLabels.indexOf(COPY_STEP_ADJUST_LABEL)} />
      {/* This step absorbed the old "Confirm what's copied" picker, so it has
          to say BOTH things: here is everything the copy will create, and
          here is where you change or drop any of it. */}
      <h1>{COPY_STEP_ADJUST_LABEL}</h1>
      <p className="lede">
        Here is everything the copy will create. Use the <b>×</b> next to a row to leave
        that row out, or tick several rows and remove them together.
      </p>
      {building.length > 0 && (
        <div className="callout cw-building" aria-live="polite">
          <span className="material-icons cw-spin">autorenew</span>
          <div>
            <b>Building the copy plan…</b>{' '}
            {building.length} of {included.length} campaign{included.length === 1 ? '' : 's'} still building
            {' '}({building.map((c) => c.name).join(', ')}). Each one appears here as it finishes.
          </div>
        </div>
      )}

      {/* One section per source campaign — per-campaign disclosure, never
          all-or-nothing: a failed build shows its error + retry while its
          siblings' grids stay fully usable. */}
      {included.map((c) => {
        const build = buildFor(c);
        return (
          <section key={c.id} style={{ marginBottom: 18 }}>
            {included.length > 1 && (
              <div className="cw-prov">
                <span className="material-icons">content_copy</span>
                Copied from <b>{c.name}</b>
              </div>
            )}
            {build.status === 'failed' && (
              <div className="gate-banner cw-buildfail">
                <span className="material-icons">error</span>
                <div className="t">
                  <b>{c.name}</b> — build failed: {build.error || 'unknown error'}
                </div>
                <button
                  type="button"
                  className="cw-btn cw-btn-secondary cw-btn-sm"
                  onClick={() => onRetryBuild(c.id)}
                >
                  Retry build
                </button>
              </div>
            )}
            {build.status === 'review' && build.launcherResult && (
              <>
                <BuildGates
                  gates={gatesByCampaign?.[c.id]}
                  campaignId={c.id}
                  onApplyUniqueName={onApplyUniqueName}
                />
                <CopyBuildGrid
                  campaign={c}
                  build={build}
                  onSaveBuild={onSaveBuild}
                  campaignName={campaignName}
                  accountId={accountId}
                  userId={userId}
                  readOnly={pushed}
                  aiOpen={openAgentFor === c.id}
                  setAiOpen={agentOpenSetterFor(c.id)}
                  startAiResize={startAiResize}
                />
              </>
            )}
          </section>
        );
      })}
      </div>

      <div className="cw-picker-selbar" ref={pushBarRef}>
        {nBlocked > 0 && <span className="gate gate-block"><span className="material-icons">block</span> {nBlocked} blocked</span>}
        {nNeed > 0 && <span className="gate gate-need"><span className="material-icons">help</span> {nNeed} need your decision</span>}
        {nBlocked === 0 && reviewed.length > 0 && <span className="gate gate-ok"><span className="material-icons">check</span> no blocking gates</span>}
        <div className="gap" />
        <button className="cw-btn cw-btn-secondary cw-btn-sm" onClick={onBack}>Back</button>
        {/* Nothing to push until every build has landed: the push step launches
            the SAVED template, and a campaign still building has none. Going
            through early left it on a step whose own bar reads "Waiting for the
            build to finish on the Confirm & adjust step" — a round trip that
            says what this button could have said in the first place. */}
        <button
          className="cw-btn cw-btn-primary cw-btn-sm"
          onClick={onContinue}
          disabled={building.length > 0}
          title={building.length > 0
            ? `Still building ${building.length} campaign${building.length === 1 ? '' : 's'} — this enables when the plan is ready.`
            : undefined}
        >
          {building.length > 0 ? 'Waiting for the build…' : 'Continue to push'}
          {' '}<span className="material-icons">east</span>
        </button>
      </div>
    </div>
  );
}
